import { netaxeptPreselectBanks, worldlinePreselectBanks } from "../views/Job/paymentProviders";
import useApi from "./useApi";
import useEnv from "./useEnv";

/**
 * Custom hook to handle payment providers related actions
 */
const usePaymentProviders = () => {
    const { getJson } = useApi();
    const { getEnv } = useEnv();

    /**
     * Get available payment methods from Tink.
     * Returns an array of payment providers.
     * 
     * @returns {Promise<Array>} - array of payment providers | empty array
     */
    const fetchTinkPaymentProviders = async () => {
        const data = await getJson(`/online/paymentproviders`);

        if (!Array.isArray(data?.providers)) {
            return [];
        }

        return data.providers;
    }


    /**
     * Get allowed Worldline payment methods.
     * 
     * @returns Array of Worldline payment methods
     */
    const getWorldlinePaymentMethods = () => {
        return worldlinePreselectBanks.providers;
    }


    /**
     * Add defined Netaxept banks to an array of banks.
     * If bank id does not match to the allowed netaxeptPreselectBanks, it is not added to the array.
     * If no banks are passed, an empty array is returned.
     * 
     * @param {*} includedNetsBanks
     * @returns array of Netaxept payment methods | empty array
     */
    const addNetaxeptPaymentMethods = (includedNetsBanks) => {
        let banks = [];

        if (includedNetsBanks?.length > 0) {
            // loop though the passed Netaxept banks and check whether or not it is in the allowed Netaxept banks list
            includedNetsBanks.forEach(netsbank => {
                netaxeptPreselectBanks.providers.forEach((bank) => {
                    // add payment method option, if it is ok 
                    if (bank.bank_id === netsbank) {
                        banks.push(bank);
                    }
                })
            });
        }

        return banks;
    }


    /**
     * Add Worldline payment methods to an array of banks.
     * If bank id does not match to the allowed worldlinePreselectBanks, it is not added to the array.
     * If no banks are passed, an empty array is returned.
     * 
     * @param {*} includedWorldlineBanks 
     * @returns 
     */
    const addWorldlinePaymentMethods = (includedWorldlineBanks) => {
        if (!Array.isArray(includedWorldlineBanks) || includedWorldlineBanks.length === 0) {
            return [];
        }

        // loop though the passed Worldline banks, and add the payment method option if is in the allowed Worldline banks list
        return getWorldlinePaymentMethods().filter(bank =>
            includedWorldlineBanks.includes(bank.bank_id)
        );
    }


    /**
     * Check if payment methods are restricted based on job status, and remove them from allowed payment methods
     * when applicable.
     * 
     * N.B. Env. variables VITE_FORBIDDEN_PAYMENT_PAYMENTMETHOD and VITE_FORBIDDEN_PAYMENT_JOBSTATUS
     * should always be used together!
     * 
     * Example usage: Paying with Vipps should not be allowed when jos is in invoicing or reminder status
     * VITE_FORBIDDEN_PAYMENT_PAYMENTMETHOD=Vipps
     * VITE_FORBIDDEN_PAYMENT_JOBSTATUS=0,1 
     *  
     * 
     * @param {*} paymentMethods - array of available payment methods
     * @returns array of allowed payment methods
     */
    const checkForRestrictions = (paymentMethods, mainstatus) => {
        let allowedPaymentMethods = paymentMethods;

        // check if payment methods and statues are restricted
        const restrictedJobStatuses = getEnv('FORBIDDEN_PAYMENT_JOBSTATUS')?.split(",") || [];
        const restrictedPaymentMethods = getEnv('FORBIDDEN_PAYMENT_PAYMENTMETHOD')?.split(",") || [];

        // check if job status is in the list of restricted statuses
        const hasRestrictedStatus = restrictedJobStatuses.includes(String(mainstatus ?? ''));
        
        if (hasRestrictedStatus) {
            // remove the restricted payment methods from the list of allowed payment methods
            allowedPaymentMethods = allowedPaymentMethods.filter(
                method => !restrictedPaymentMethods.includes(method.name)
            );
        }

        return allowedPaymentMethods;
    }


    /**
     * Check array for Swish payment method.
     * If Swish if found, rearrange the array so that it is always the first option.
     * 
     * @param {*} banks 
     * @returns array of payment methods
     */
    const rearrangeArrayForSwish = (banks) => {

        const swishIndex = banks.findIndex(bank => bank.bank_id === 'SwishE');

        if (swishIndex <= 0) {
            return banks;
        }

        // if Swish is included in the payment methods, always make it the first option if it isn't already
        const [swishBank] = banks.splice(swishIndex, 1);
        banks.unshift(swishBank);

        return banks;
    }


    return {
        fetchTinkPaymentProviders,
        addNetaxeptPaymentMethods,
        addWorldlinePaymentMethods,
        checkForRestrictions,
        rearrangeArrayForSwish,
        getWorldlinePaymentMethods
    }
}

export default usePaymentProviders;